<template>
  <div :style="{ visibility: Display ? 'visible' : 'hidden' }" class="cs-root">

    <!-- ── Header ── -->
    <div class="cs-header">
      <button class="cs-close" @click="close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="18" height="18">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
      <span class="cs-title">Comments</span>
      <span class="cs-count">{{ totalCount }}</span>

      <!-- Spinner — visible only while loading -->
      <svg v-if="loading" class="cs-spinner" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="csGrad">
            <stop offset="0%"   stop-color="#00e5ff" stop-opacity="1"/>
            <stop offset="100%" stop-color="#00e5ff" stop-opacity="0.15"/>
          </linearGradient>
        </defs>
        <circle cx="10" cy="10" r="8" stroke="url(#csGrad)" stroke-width="2" fill="none"/>
      </svg>

      <!-- Reload button — always visible -->
      <button
        class="cs-reload"
        @click="getComments (new Date())"
        :disabled="loading"
        :title="loadError ? 'Failed — retry' : 'Reload comments'"
        aria-label="Reload comments"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="14" height="14">
          <path d="M1 4v6h6"/><path d="M23 20v-6h-6"/>
          <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/>
        </svg>
      </button>
    </div>

    <!-- ── List ── -->
    <div class="cs-list" ref="listRef">
      <div v-if="!comments.length" class="cs-empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="36" height="36">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <p>Be the first to comment</p>
      </div>

      <div
        v-for="(comment, ci) in comments"
        :key="comment._id || ci"
        class="cs-comment"
      >
        <!-- Top-level bubble -->
        <div class="cs-bubble">
          <div class="cs-avatar" :style="avatarStyle(comment.userId)">
            <img
              v-if="userMeta(comment.userId).profilImgMini1"
              :src="$store.state.baseUrl + 'images/' + userMeta(comment.userId).profilImgMini1"
            />
            <span v-else>{{ initial(comment.userId) }}</span>
          </div>

          <div class="cs-body">
            <div class="cs-meta">
              <span @click="$emit('publisher-click',comment.userId)"
                  class="cs-name">{{ userMeta(comment.userId).pseudo || comment.userId }}</span>
              <span class="cs-time">{{ ago(comment.createdAt) }}</span>
            </div>
            <p class="cs-text" v-html="sanitize(comment.text)"></p>
            <div class="cs-actions">
              <button
                class="cs-act"
                :class="{ 'cs-act--liked': hasLiked(comment.likes) }"
                @click="toggleLike(comment)"
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
                <span>{{ (comment.likes || []).length }}</span>
              </button>

              <button class="cs-act cs-act--reply" @click="showReplies(comment)">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <span>{{ (comment.comments || []).length }}</span>
              </button>

              <button class="cs-act cs-act--label" @click="setReply(comment)">Reply</button>
              <template v-if="comment.userId === myUserId">
              <button class="cs-act cs-act--edit" @click="editComment(comment)" title="Edit">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
              <button class="cs-act cs-act--delete" @click="deleteComment(comment)" title="Delete">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            </template>
            </div>
          </div>
        </div>

        <!-- Sub-comments -->
        <transition name="slide">
          <div v-if="expandedId === comment._id" class="cs-replies">
            <div
              v-for="(sub, si) in (comment.comments || [])"
              :key="sub._id || si"
              class="cs-reply"
            >
              <div class="cs-avatar cs-avatar--sm" :style="avatarStyle(sub.userId)">
                <img
                  v-if="userMeta(sub.userId).profilImgMini1"
                  :src="$store.state.baseUrl + 'images/' + userMeta(sub.userId).profilImgMini1"
                />
                <span v-else>{{ initial(sub.userId) }}</span>
              </div>
              <div class="cs-body">
                <div class="cs-meta">
                  <span class="cs-name" @click="$emit('publisher-click',sub.userId)">
                    {{ userMeta(sub.userId).pseudo || sub.userId }}</span>
                  <span class="cs-time">{{ ago(sub.createdAt) }}</span>
                </div>
                <p class="cs-text" v-html="sanitize(sub.text)"></p>
                <div class="cs-actions">
                  <button
                    class="cs-act"
                    :class="{ 'cs-act--liked': hasLiked(sub.likes) }"
                    @click="toggleSubLike(comment, sub)"
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                    </svg>
                    <span>{{ (sub.likes || []).length }}</span>
                  </button>
                  <button class="cs-act cs-act--label" @click="setReply(comment, sub)">Reply</button>
                  <template v-if="sub.userId === myUserId">

                  <button class="cs-act cs-act--edit" @click="editReply(comment, sub)" title="Edit">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button class="cs-act cs-act--delete" @click="deleteReply(comment, sub)" title="Delete">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      <path d="M10 11v6"/><path d="M14 11v6"/>
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                    </svg>
                  </button>
                </template>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- ── Single compose bar ── -->
    <div class="cs-compose">
      <!-- Reply-to badge — shown when replying to someone -->
      <transition name="pop">
        <!-- Edit badge -->
        <div v-if="editTarget" class="cs-reply-badge cs-edit-badge">
          <span>
            ✎ editing
            <strong>{{ editTarget.type === 'reply' ? 'reply' : 'comment' }}</strong>
            — {{ truncateText(editTarget.type === 'reply' ? splitAnchorText(editTarget.sub.text) : editTarget.comment.text) }}
          </span>
          <button @click="clearEdit" aria-label="Cancel edit">✕</button>
        </div>

        <!-- Reply badge -->
        <div v-else-if="replyTarget" class="cs-reply-badge">
          <span>↩ replying to <strong>{{ userMeta(replyTarget.userId).pseudo || replyTarget.userId }}</strong></span>
          <button @click="clearReply" aria-label="Cancel reply">✕</button>
        </div>
      </transition>

      <div class="cs-compose-row">
        <div class="cs-avatar cs-avatar--sm" :style="avatarStyle(myUserId)">
          <img
            v-if="userMeta(myUserId).profilImgMini1"
            :src="$store.state.baseUrl + 'images/' + userMeta(myUserId).profilImgMini1"
          />
          <span v-else>{{ initial(myUserId) }}</span>
        </div>

        <div class="cs-input-wrap">
          <textarea
            v-model="inputText"
            class="cs-input"
            :placeholder="replyTarget ? 'Reply to ' + (userMeta(replyTarget.userId).pseudo || 'comment') + '…' : 'Write a comment…'"
            @keydown.enter.exact.prevent="submit"
            ref="mainInputRef"
            rows="1"
          />
          <button
            class="cs-send"
            @click="submit"
            :disabled="!inputText.trim() || submitting"
            aria-label="Send"
          >
            <svg style="min-width: 24px; width: 24px;" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'CommentSection',
  data() {
    return {
      Display:false,
      data:[],
      subjectId:'',
      comments:    [],      // local reactive deep-copy of prop
      inputText:   '',
      replyTarget: null,    // comment object being replied to, or null
      expandedId:  null,    // _id of the thread currently showing replies
      submitting:  false,
      loading: false,
      loadError: false,
      editTarget: null,   // { type: 'comment'|'reply', comment, sub? }
    }
  },

  computed: {
    myUserId() {
      return this.$store.state.user.uid
    },
    totalCount() {
      let n = this.comments.length
      for (let i = 0; i < this.comments.length; i++) {
        n += (this.comments[i].comments || []).length
      }
      return n
    },
  },

  watch: {
    data: {
      immediate: true,
      deep:      true,
      handler(val) {
        this.comments = val ? JSON.parse(JSON.stringify(val)) : []
      },
    },
    inputText() {
      this.$nextTick(() => {
        const el = this.$refs.mainInputRef
        if (!el) return
        el.style.height = 'auto'
        el.style.height = el.scrollHeight + 'px'
      })
    }
  },

  methods: {
    // ── Store helpers ─────────────────────────────────────────────────────────
    userMeta(userId) {
      return this.$store.state.users?.[userId] || {}
    },

    // ── Formatting ────────────────────────────────────────────────────────────
    initial(userId) {
      const name = this.userMeta(userId).pseudo || userId || '?'
      return name[0].toUpperCase()
    },

    avatarStyle(userId) {
      let hash = 0
      const s  = userId || ''
      for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash)
      const hue = ((hash % 360) + 360) % 360
      return { background: `hsl(${hue},50%,36%)` }
    },

    ago(date) {
      if (!date) return ''
      const s = (Date.now() - new Date(date)) / 1000 | 0
      if (s < 60)    return `${s}s`
      if (s < 3600)  return `${s / 60 | 0}m`
      if (s < 86400) return `${s / 3600 | 0}h`
      return `${s / 86400 | 0}d`
    },

    hasLiked(likes) {
      if (!likes || !this.myUserId) return false
      for (let i = 0; i < likes.length; i++) {
        if (likes[i] === this.myUserId) return true
      }
      return false
    },

    // ── Reply state ───────────────────────────────────────────────────────────
    setReply(comment, sub) {
      this.replyTarget = sub ||comment
      this.replyTarget.parent = comment

      this.showReplies(comment)
      this.$nextTick(() => {
        if (this.$refs.mainInputRef) this.$refs.mainInputRef.focus()
      })
    },   
    showReplies(comment) {
      this.expandedId  = comment._id   // open the thread so replies are visible
    },

    clearReply() {
      this.replyTarget = null
      this.inputText   = ''
    },
    show (pub) {
      this.Display = true
      this.data = []
      this.comments=[]
      this.subjectId = pub._id
      this.getComments(new Date())
    },
    close () {
      this.Display = false
    },
    async getComments (from) {
        this.loading= true
        try {
          const res = await this.$axios.post('/publication.php',{type:"commentList",publicationId:this.subjectId, from:from})
          console.log(res.data)
          this.data = res.data
          // set userIds
          if(this.data.length === 0) return 
          const usersToGet = this.extractAllUserIds(this.data)
          const k = this
          //-- get friends infos ------------------------------
          if(usersToGet.length > 0) {
            const res2 = await this.$axios.post('user.php',{type:"getUsersMetaData",users:usersToGet})
            const users = res2.data || []
            users.forEach(us => {
              k.$store.commit('setAnUser',us)
            })
          }
      } catch (e) {
        console.log(e.response)
      } finally { this.loading = false}
    },
    extractAllUserIds(comments) {
      const seen = new Set()
      const ids  = []

      function add(id) {
        if (id && !seen.has(id)) { seen.add(id); ids.push(id) }
      }

      for (let i = 0; i < comments.length; i++) {
        const c = comments[i]
        add(c.userId)

        const likes = c.likes || []
        for (let j = 0; j < likes.length; j++) add(likes[j])

        const subs = c.comments || []
        for (let k = 0; k < subs.length; k++) {
          add(subs[k].userId)
          const subLikes = subs[k].likes || []
          for (let l = 0; l < subLikes.length; l++) add(subLikes[l])
        }
      }

      return ids
    },
    // ── Single submit — routes to comment or reply based on replyTarget ───────
    async submit() {
      const text = this.inputText.trim()
      if (!text || this.submitting) return
      this.submitting = true
      try {
        if (this.editTarget) {
          await this._submitEdit(text)
        } else if (this.replyTarget) {
          await this._submitReply(text)
        } else {
          await this._submitComment(text)
        }
        this.inputText  = ''
        this.replyTarget = null
        this.editTarget  = null
      } catch (e) {
        console.error(e?.response ?? e)
      } finally {
        this.submitting = false
      }
    },

    async _submitComment(text) {
      const res = await this.$axios.post('/publication.php',{type:"comment",text:text,publicationId:this.subjectId})
      console.log(res.data)
      this.comments.push(res.data || {
        _id: Date.now().toString(), userId: this.myUserId,
        text, likes: [], comments: [], createdAt: new Date(),
      })
      this.$nextTick(() => {
        if (this.$refs.listRef) this.$refs.listRef.scrollTop = this.$refs.listRef.scrollHeight
      })
    },

    async _submitReply(text) {
      let parent  = this.replyTarget.parent ? this.replyTarget.parent : this.replyTarget // if no parent specified then the replying message is the parent
      let finalText = `<a>${this.userMeta(this.replyTarget.userId).pseudo} </a>${text}`
      const res = await this.$axios.post('/publication.php',{type:"commentComment",text:finalText,
        comId:parent._id}) // return new parent.comments
      console.log(res.data)
      parent.comments = res.data
      /*if (!parent.comments) parent.comments = []
       parent.comments.push(res.data || {
        _id: Date.now().toString(), userId: this.myUserId,
        text, likes: [], createdAt: new Date(),
      }) */
    },

    // ── Likes ─────────────────────────────────────────────────────────────────
    async toggleLike(comment) {
      const uid = this.myUserId
      if (!uid) return
      if (!comment.likes) comment.likes = []
      const idx = comment.likes.indexOf(uid)
      // optimistic update
      if (idx === -1) comment.likes.push(uid)
      else            comment.likes.splice(idx, 1)
      try {
        const res = await this.$axios.post('/publication.php',{type: idx ===-1 ? "likeComment" : "unlikeComment",comId:comment._id})
        console.log(res)
      } catch (e) {
        // revert
        if (idx === -1) comment.likes.pop()
        else            comment.likes.splice(idx, 0, uid)
        console.error(e?.response ?? e)
      }
    },

    async toggleSubLike(parentComment, sub) {
      const uid = this.myUserId
      if (!uid) return
      if (!sub.likes) sub.likes = []
      const idx = sub.likes.indexOf(uid)
      if (idx === -1) sub.likes.push(uid)
      else            sub.likes.splice(idx, 1)
      try {
        const res = await this.$axios.post('/publication.php',{type:idx === -1 ? "likeComOfCom":"unlikeComOfCom",
        comOfcomId:sub._id,
        comId:parentComment._id})
        console.log(res.data)
      } catch (e) {
        if (idx === -1) sub.likes.pop()
        else            sub.likes.splice(idx, 0, uid)
        console.error(e?.response ?? e)
      }
    },

    async _submitEdit(text) {
      const { type, comment, sub } = this.editTarget

      if (type === 'comment') {
        const oldText    = comment.text
        comment.text     = text          // optimistic update
        try {
          const res = await this.$axios.post('/publication.php',{type:"modifyComment",comId:comment._id,
            text:text, publicationId:this.subjectId})
            console.log(res.data)
        } catch (e) {
          comment.text = oldText         // revert
          throw e
        }

      } else {
        const oldText = sub.text
        const match = oldText.match(/^(<a\b[^>]*>.*?<\/a>)([\s\S]*)$/i); // cut off the name of the replying message owner
        
        sub.text      = match ? match[1]+text : text           // optimistic update
        try {
          const res = await this.$axios.post('/publication.php',{type:"modifyComOfCom",text:sub.text,
            comOfcomId:sub._id,
            comId:comment._id})
          console.log(res.data)
        } catch (e) {
          sub.text = oldText             // revert
          throw e
        }
      }
    },

    // ── Edit ─────────────────────────────────────────────────────────────────────
    editComment(comment) {
      this.editTarget  = { type: 'comment', comment }
      this.replyTarget = null
      this.inputText   = comment.text
      this.$nextTick(() => {
        if (this.$refs.mainInputRef) this.$refs.mainInputRef.focus()
      })
    },

    editReply(parentComment, sub) {
      this.editTarget  = { type: 'reply', comment: parentComment, sub }
      this.replyTarget = null
      const match = sub.text.match(/^(<a\b[^>]*>.*?<\/a>)([\s\S]*)$/i); // cut off the name of the replying message owner
      this.inputText   = match ? match[2] : sub.text
      this.$nextTick(() => {
        if (this.$refs.mainInputRef) this.$refs.mainInputRef.focus()
      })
    },

    // ── Delete ────────────────────────────────────────────────────────────────────
    async deleteComment(comment) {
      const idx = this.comments.indexOf(comment)
      if (idx === -1) return
      this.comments.splice(idx, 1)      // optimistic update
      try {
        const res = await this.$axios.post('/publication.php',{type:"deleteComment",comId:comment._id,
          publicationId:this.subjectId})
        console.log(res.data)
      } catch (e) {
        this.comments.splice(idx, 0, comment)  // revert
        console.error(e?.response ?? e)
      }
    },

    async deleteReply(parentComment, sub) {
      const replies = parentComment.comments || []
      const idx     = replies.indexOf(sub)
      if (idx === -1) return
      replies.splice(idx, 1)            // optimistic update
      try {
        const res = await this.$axios.post('/publication.php',{type:"deleteComOfCom",
        comOfcomId:sub._id,
        comId:parentComment._id})
        console.log(res.data)
      } catch (e) {
        replies.splice(idx, 0, sub)     // revert
        console.error(e?.response ?? e)
      }
    },
    clearEdit() {
      this.editTarget = null
      this.inputText  = ''
    },
    truncateText(text, max = 32) {
      if (!text) return ''
      return text.length > max ? text.slice(0, max) + '…' : text
    },
    splitAnchorText(text) {
      const match = text.match(/^(<a\b[^>]*>.*?<\/a>)([\s\S]*)$/i);
      return match ? match[2] : text
    },
    sanitize(text) {
      if (!text) return ''

      // 1. Escape all HTML to neutralize any injection
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')

      // 2. Restore only your internal <a>pseudo</a> mention pattern (no attributes)
      const withMentions = escaped.replace(
        /&lt;a&gt;(.*?)&lt;\/a&gt;/g,
        '<a>$1</a>'
      )

      // 3. Detect plain URLs and turn them into safe clickable links
      return withMentions.replace(
        /https?:\/\/[^\s<>"']+/g,
        url => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
      )
    },

  },
}
</script>

<style scoped>
/* @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');
 */
 .cs-root {
  position: sticky;
  top: 0px;
  left: 0px;
  width: 500px;
  max-width: 100%;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  background: #07090f;
  font-family: 'DM Sans', sans-serif;
  color: #e2e8f0;
}

.cs-header {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  flex-shrink: 0;
}
.cs-header > * + * { margin-left: 12px; }

.cs-close {
  background: none;
  border: none;
  padding: 5px;
  cursor: pointer;
  color: rgba(255,255,255,0.45);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s;
  margin-right: 2px;
}
.cs-close:hover { color: #e2e8f0; background: rgba(255,255,255,0.08); }
.cs-close svg { width: 20px; height: 20px; }

.cs-title {
  font-size: 17px;
  font-weight: 600;
  color: #f1f5f9;
  flex: 1;
}
.cs-count {
  font-family: 'DM Mono', monospace;
  font-size: 13px;
  background: rgba(0,229,255,0.12);
  color: #00e5ff;
  padding: 3px 10px;
  border-radius: 99px;
}

.cs-spinner {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}
.cs-spinner circle {
  transform-origin: 50% 50%;
  animation: cs-spin 0.5s infinite linear;
}
@keyframes cs-spin {
  to { transform: rotate(360deg); }
}

.cs-reload {
  background: none;
  border: none;
  padding: 5px;
  cursor: pointer;
  color: rgba(255,255,255,0.35);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s;
}
.cs-reload svg { width: 16px; height: 16px; }
.cs-reload:hover:not(:disabled) { color: #00e5ff; background: rgba(0,229,255,0.08); }
.cs-reload:disabled { opacity: 0.35; cursor: default; }

.cs-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 0 4px;
  scrollbar-width: thin;
}
.cs-list::-webkit-scrollbar       { width: 3px; }
.cs-list::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }

.cs-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 52px 24px;
  color: rgba(255,255,255,0.2);
}
.cs-empty > * + * { margin-top: 12px; }
.cs-empty svg { width: 40px; height: 40px; }
.cs-empty p { margin: 0; font-size: 14px; }

.cs-comment {
  padding: 8px 16px;
}

.cs-bubble {
  display: flex;
  align-items: flex-start;
}
.cs-bubble > * + * { margin-left: 12px; }

.cs-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
}
.cs-avatar--sm { width: 30px; height: 30px; font-size: 11px; }
.cs-avatar img { width: 100%; height: 100%; object-fit: cover; }

.cs-body { flex: 1; min-width: 0; }

.cs-meta {
  display: flex;
  align-items: baseline;
  margin-bottom: 3px;
}
.cs-meta > * + * { margin-left: 8px; }

.cs-name {
  font-size: 13px;
  font-weight: 600;
  color: #f1f5f9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}
.cs-name:hover {
  color:gray;
  cursor: pointer;
}
.cs-time {
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  color: rgba(255,255,255,0.28);
  flex-shrink: 0;
}
.cs-text {
  margin: 0 0 6px;
  font-size: 14px;
  line-height: 1.5;
  color: #cbd5e1;
  word-break: break-word;
  white-space: pre-wrap;
}
:deep(.cs-text a) {
  color: #0099cc;
  font-weight: bold;
  cursor: default;
}
:deep(.cs-text a[href]) {
  color: #00e5ff;
  font-weight: normal;
  font-style: italic;
  cursor: pointer;
  text-decoration: none;
  text-underline-offset: 2px;
}

.cs-actions {
  display: flex;
  align-items: center;
}
.cs-actions > * + * { margin-left: 14px; }

.cs-act {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  color: rgba(255,255,255,0.32);
  transition: color 0.15s;
}
.cs-act > * + * { margin-left: 5px; }
.cs-act svg { width: 14px; height: 14px; }
.cs-act:hover        { color: rgba(255,255,255,0.65); }
.cs-act--liked       { color: #ff4d6d; }
.cs-act--liked:hover { color: #ff6b85; }
.cs-act--reply       { color: rgba(0,229,255,0.45); }
.cs-act--reply:hover { color: #00e5ff; }
.cs-act--label {
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255,255,255,0.35);
}
.cs-act--label:hover { color: rgba(255,255,255,0.7); }
.cs-act--edit        { color: rgba(255,255,255,0.25); }
.cs-act--edit:hover  { color: rgba(0,229,255,0.8); }
.cs-act--delete      { color: rgba(255,255,255,0.25); }
.cs-act--delete:hover{ color: #ff4d6d; }

.cs-replies {
  margin-top: 10px;
  margin-left: 50px;
  border-left: 2px solid rgba(255,255,255,0.06);
  padding-left: 14px;
  display: flex;
  flex-direction: column;
}
.cs-replies > * + * { margin-top: 12px; }

.cs-reply {
  display: flex;
  align-items: flex-start;
}
.cs-reply > * + * { margin-left: 10px; }

.slide-enter-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.slide-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.slide-enter-from   { opacity: 0; transform: translateY(-5px); }
.slide-leave-to     { opacity: 0; transform: translateY(-3px); }

.cs-compose {
  flex-shrink: 0;
  border-top: 1px solid rgba(255,255,255,0.07);
  background: rgba(8,11,22,0.97);
  padding: 10px 14px 16px;
}

.cs-compose-row {
  display: flex;
  align-items: center;
}
.cs-compose-row > * + * { margin-left: 10px; }

.cs-reply-badge {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(0,229,255,0.07);
  border: 1px solid rgba(0,229,255,0.15);
  border-radius: 6px;
  padding: 6px 12px;
  margin-bottom: 10px;
  font-size: 13px;
  color: rgba(0,229,255,0.8);
}
.cs-reply-badge strong { color: #00e5ff; }
.cs-reply-badge button {
  background: none;
  border: none;
  color: rgba(0,229,255,0.5);
  cursor: pointer;
  font-size: 14px;
  padding: 0 0 0 10px;
  line-height: 1;
  transition: color 0.15s;
}
.cs-reply-badge button:hover { color: #00e5ff; }

.cs-edit-badge {
  background: rgba(255,200,0,0.07);
  border-color: rgba(255,200,0,0.18);
  color: rgba(255,200,0,0.85);
}
.cs-edit-badge strong { color: #ffd000; }
.cs-edit-badge button { color: rgba(255,200,0,0.45); }
.cs-edit-badge button:hover { color: #ffd000; }

.pop-enter-active  { transition: opacity 0.18s ease, transform 0.18s ease; }
.pop-leave-active  { transition: opacity 0.12s ease, transform 0.12s ease; }
.pop-enter-from    { opacity: 0; transform: scaleY(0.85); transform-origin: top; }
.pop-leave-to      { opacity: 0; transform: scaleY(0.85); transform-origin: top; }

.cs-input-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 22px;
  padding: 0 5px 0 15px;
  transition: border-color 0.2s, background 0.2s;
}
.cs-input-wrap:focus-within {
  border-color: rgba(0,229,255,0.30);
  background: rgba(0,229,255,0.04);
}
.cs-input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: #e2e8f0;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  padding: 11px 0;
  min-width: 0;
  resize: none;
  overflow-y: hidden;
  line-height: 1.5;
}
.cs-input::placeholder { color: rgba(255,255,255,0.22); }

.cs-send {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: none;
  background: linear-gradient(135deg, #00e5ff, #0099cc);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.15s;
}
.cs-send svg { width: 17px; height: 17px; }
.cs-send:disabled              { opacity: 0.3; cursor: default; }
.cs-send:not(:disabled):hover  { transform: scale(1.08); }
.cs-send:not(:disabled):active { transform: scale(0.93); }

@media (max-width: 640px) {
  .cs-root {
    position: fixed;
    width: 100%;
    min-width: 320px;
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    background: #07090f;
    color: #e2e8f0;
  }
}

/* ════════════════════════════════════════════════════
   Small-screen override — restore original proportions
   ════════════════════════════════════════════════════ */
@media (max-width: 340px) {
  .cs-header { padding: 12px 14px; }
  .cs-header > * + * { margin-left: 10px; }
  .cs-close svg { width: 18px; height: 18px; }

  .cs-title  { font-size: 15px; }
  .cs-count  { font-size: 11px; padding: 2px 8px; }

  .cs-spinner { width: 16px; height: 16px; }
  .cs-reload svg { width: 14px; height: 14px; }

  .cs-list { padding: 10px 0 4px; }

  .cs-comment { padding: 6px 14px; }
  .cs-bubble > * + * { margin-left: 10px; }

  .cs-empty svg { width: 36px; height: 36px; }
  .cs-empty p { font-size: 13px; }

  .cs-avatar     { width: 34px; height: 34px; font-size: 13px; }
  .cs-avatar--sm { width: 26px; height: 26px; font-size: 10px; }

  .cs-meta > * + * { margin-left: 7px; }
  .cs-name { font-size: 12px; max-width: 150px; }
  .cs-time { font-size: 10px; }
  .cs-text { font-size: 13px; margin: 0 0 5px; }

  .cs-actions > * + * { margin-left: 12px; }
  .cs-act        { font-size: 11px; }
  .cs-act > * + * { margin-left: 4px; }
  .cs-act svg    { width: 13px; height: 13px; }
  .cs-act--label { font-size: 11px; }

  .cs-replies { margin-left: 44px; padding-left: 12px; }
  .cs-replies > * + * { margin-top: 10px; }
  .cs-reply > * + * { margin-left: 8px; }

  .cs-compose { padding: 8px 12px 14px; }
  .cs-compose-row > * + * { margin-left: 8px; }

  .cs-reply-badge { font-size: 12px; padding: 5px 10px; margin-bottom: 8px; }
  .cs-reply-badge button { font-size: 13px; }

  .cs-input-wrap { padding: 0 4px 0 13px; border-radius: 20px; }
  .cs-input      { font-size: 13px; padding: 9px 0; }

  .cs-send     { width: 30px; height: 30px; border-radius: 8px; }
  .cs-send svg { width: 16px; height: 16px; }
}
</style>